import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, User } from 'lucide-react';
import type { Player } from '../../types/player';

interface PlayerSelectorProps {
  position: 1 | 2;
  onSelectPlayer: (player: Player | null) => void;
  selectedPlayer: Player | null;
  availablePlayers: Player[];
}

const PlayerSelector: React.FC<PlayerSelectorProps> = ({
  position,
  onSelectPlayer,
  selectedPlayer,
  availablePlayers
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredPlayers, setFilteredPlayers] = useState<Player[]>([]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPlayers(availablePlayers.slice(0, 50)); // Show first 50 players
    } else {
      const filtered = availablePlayers.filter(player => {
        const playerName = player.player_name || player.nombre_transfermarkt || '';
        return playerName.toLowerCase().includes(searchTerm.toLowerCase());
      }).slice(0, 50);
      setFilteredPlayers(filtered);
    }
  }, [searchTerm, availablePlayers]);

  const handleSelectPlayer = (player: Player) => {
    onSelectPlayer(player);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClearSelection = () => {
    onSelectPlayer(null);
    setSearchTerm('');
  };

  const getPlayerName = (player: Player) => {
    const name = player.player_name || player.nombre_transfermarkt || t('comparison.noPlayersFound');
    return name.split('(')[0].trim();
  };

  const getPlayerTeam = (player: Player) => {
    return player.current_club_name || player.equipo_transfermarkt || '';
  };

  const getPlayerImage = (player: Player) => {
    return player.player_image_url || player.url_foto_jugador;
  };

  const borderColor = position === 1 ? 'border-blue-500' : 'border-red-500';
  const bgColor = position === 1 ? 'bg-blue-50' : 'bg-red-50';
  const textColor = position === 1 ? 'text-blue-600' : 'text-red-600';

  return (
    <div className={`bg-white rounded-xl shadow-md p-6 border-2 ${borderColor}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-xl font-bold ${textColor}`}>
          {t('comparison.player' + position)}
        </h3>
        {selectedPlayer && (
          <button
            onClick={handleClearSelection}
            className="text-gray-500 hover:text-red-600 transition-colors"
            aria-label="Clear selection"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {selectedPlayer ? (
        <div className={`${bgColor} rounded-lg p-4`}>
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
              {getPlayerImage(selectedPlayer) ? (
                <img
                  src={getPlayerImage(selectedPlayer)}
                  alt={getPlayerName(selectedPlayer)}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement!.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg></div>';
                  }}
                />
              ) : (
                <User className="w-10 h-10 text-gray-400" />
              )}
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-lg text-gray-900">
                {getPlayerName(selectedPlayer)}
              </h4>
              <p className="text-gray-600 text-sm">
                {getPlayerTeam(selectedPlayer)}
              </p>
              <p className="text-gray-500 text-xs mt-1">
                {selectedPlayer.position || selectedPlayer.posicion_transfermarkt}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`w-full p-4 border-2 border-dashed ${borderColor} rounded-lg hover:bg-gray-50 transition-colors`}
          >
            <div className="flex flex-col items-center space-y-2 text-gray-500">
              <Search className="w-8 h-8" />
              <span className="text-sm font-medium">
                {t('comparison.selectPlayer')}
              </span>
            </div>
          </button>

          {isOpen && (
            <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-96 overflow-hidden">
              <div className="p-3 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('comparison.searchPlaceholder')}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
              </div>

              <div className="overflow-y-auto max-h-80">
                {filteredPlayers.length > 0 ? (
                  filteredPlayers.map((player) => (
                    <button
                      key={player.player_id || player.id_transfermarkt}
                      onClick={() => handleSelectPlayer(player)}
                      className="w-full p-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-100 last:border-0"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0">
                          {getPlayerImage(player) ? (
                            <img
                              src={getPlayerImage(player)}
                              alt={getPlayerName(player)}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.parentElement!.innerHTML = '<svg class="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>';
                              }}
                            />
                          ) : (
                            <User className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {getPlayerName(player)}
                          </p>
                          <p className="text-sm text-gray-600 truncate">
                            {getPlayerTeam(player)} • {player.position || player.posicion_transfermarkt}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <Search className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>{t('comparison.noPlayersFound')}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayerSelector;
